import { useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import { useAuth } from '@opendoor/auth-fe';
import type { TransactionState } from '__generated__/athena';

import { getAthenaClient } from '../components/api';

// Transaction states that indicate an offer is no longer active
const INACTIVE_STATES: TransactionState[] = [
  'CANCELED',
  'BUYER_WALKED',
  'REJECTED',
  'CONTRACT_FELL_THROUGH',
  'BACKUP_EXPIRED',
  'CLOSED',
  'UNKNOWN',
];

export const useHasActiveBuyerOffers = (enabled = true) => {
  const { authentication } = useAuth();
  const [hasActiveOffers, setHasActiveOffers] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const idleCallbackScheduled = useRef(false);

  useEffect(() => {
    // Don't fetch again if we already have the result
    if (hasFetched) {
      return;
    }

    // wait for auth to finish loading before checking offers
    if (
      !(authentication.state === 'authenticated' || authentication.state === 'not_authenticated')
    ) {
      return;
    }

    // If not authenticated, user has no active offers
    if (authentication.state === 'not_authenticated') {
      setHasActiveOffers(false);
      setIsLoading(false);
      setHasFetched(true);
      return;
    }

    async function checkActiveOffers() {
      try {
        const result = await getAthenaClient().BuyerOffersMinimal();
        const offers = result?.customerAccount?.buying?.offers;

        if (!offers || offers.length === 0) {
          setHasActiveOffers(false);
          setIsLoading(false);
          setHasFetched(true);
          return;
        }

        // Check if there's at least one active offer
        const hasActive = offers.some(
          (o) => !INACTIVE_STATES.includes(o.transactionState as TransactionState),
        );

        setHasActiveOffers(hasActive);
        setIsLoading(false);
        setHasFetched(true);
      } catch (e) {
        Sentry.captureException(e);
        setHasActiveOffers(false);
        setIsLoading(false);
        setHasFetched(true);
      }
    }

    // If dropdown is open, fetch immediately
    if (enabled) {
      checkActiveOffers();
      return;
    }

    // Schedule idle callback only once
    if (
      !idleCallbackScheduled.current &&
      typeof window !== 'undefined' &&
      'requestIdleCallback' in window
    ) {
      idleCallbackScheduled.current = true;
      let idleCallbackId: number | null = window.requestIdleCallback(() => {
        checkActiveOffers();
        idleCallbackId = null; // Clear reference after execution
      });
      return () => {
        // Only cancel if it hasn't executed yet
        if (idleCallbackId !== null) {
          window.cancelIdleCallback(idleCallbackId);
        }
        idleCallbackScheduled.current = false;
      };
    }

    // If requestIdleCallback is not supported or already scheduled, don't auto-fetch
    // User will fetch on dropdown open instead
    return;
  }, [enabled, authentication.state, hasFetched]);

  return { hasActiveOffers, isLoading };
};
